// Ligne de résultat de la recherche universelle, commune au champ de l'en-tête
// et à la page de résultats.
import { Link } from 'react-router-dom';
import { CalendarDays, CheckSquare, FileText, FolderOpen, Inbox, Mail, User, Users } from 'lucide-react';
import { cn } from '../../lib/cn';
import {
  shouldSpaActivateSearchHit,
  universalSearchHref,
  type UniversalSearchHit,
  type UniversalSearchKind,
} from '../../lib/universalSearch';
import type { DossierStage, DossierType } from '../../services/dossiers/dossierService';
import { TypeBadge, StageBadge } from '../dossiers/dossierBadges';

export const SEARCH_KIND_ICON: Record<UniversalSearchKind, typeof FolderOpen> = {
  dossier: FolderOpen,
  contact: User,
  ticket: Inbox,
  file: FileText,
  email: Mail,
  calendar: CalendarDays,
  task: CheckSquare,
  collaborator: Users,
};

export function SearchHitLink({
  hit,
  selected = false,
  onSelect,
  onHover,
}: {
  hit: UniversalSearchHit;
  selected?: boolean;
  /** Sans `onSelect`, le lien navigue normalement (page de résultats). */
  onSelect?: () => void;
  onHover?: () => void;
}) {
  const Icon = SEARCH_KIND_ICON[hit.kind];
  return (
    <Link
      to={universalSearchHref(hit)}
      onClick={(e) => {
        if (!onSelect || !shouldSpaActivateSearchHit(e)) return;
        e.preventDefault();
        onSelect();
      }}
      onMouseEnter={onHover}
      className={cn(
        'flex items-center gap-3 w-full px-4 py-2.5 text-left transition-colors',
        selected ? 'bg-cime-primary/5' : 'hover:bg-neutral-50',
      )}
    >
      <Icon className="w-4 h-4 text-cime-primary shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-cime-text-primary truncate">
            {hit.title}
          </span>
          {hit.kind === 'contact' && hit.contactCategoryLabel ? (
            <span className="text-[10px] text-cime-text-muted shrink-0">
              {hit.contactCategoryLabel}
            </span>
          ) : null}
        </div>
        {hit.subtitle ? (
          <p className={cn(
            'text-[11px] text-cime-text-muted truncate',
            hit.kind === 'dossier' && 'tabular-nums',
          )}>
            {hit.subtitle}
          </p>
        ) : null}
      </div>
      {hit.kind === 'dossier' ? (
        <div className="flex items-center gap-1.5 shrink-0">
          {hit.archivedAt ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border bg-neutral-100 text-neutral-600 border-neutral-200">
              Archivé
            </span>
          ) : null}
          {hit.dossierType ? (
            <TypeBadge type={hit.dossierType as DossierType} className="text-[10px]" />
          ) : null}
          {hit.dossierStage ? (
            <StageBadge stage={hit.dossierStage as DossierStage} className="text-[10px]" />
          ) : null}
        </div>
      ) : null}
    </Link>
  );
}
